<script setup lang="ts">
//#region imports
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import UploadFile from '@/components/UploadFile.vue';
import { isErrorResponse } from '@/types/core';
import type { EmitsToListeners, FormErrors, UploadedFile } from '@/types/core';
//#endregion

//#region types
interface Props {
  paymentId?: string;
  affiliateEmail?: string;
}

interface Emits {
  success: [];
  close: [];
}

export interface DeclineExternalPaymentParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  paymentId: '',
  affiliateEmail: '',
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const isFilesLoading = ref(false);
const errors = ref<FormErrors>({});
const files = ref<UploadedFile[]>([]);
const form = ref({
  comment: '',
});
//#endregion

//#region methods
const open = () => {
  form.value.comment = '';
  files.value = [];
  errors.value = {};
  isVisible.value = true;
};

const handleClose = () => {
  isVisible.value = false;
  emit('close');
};

const handleDecline = async () => {
  isLoading.value = true;
  errors.value = {};

  try {
    await api.cancelExternalPayment(props.paymentId, {
      comment: form.value.comment,
      file_ids: files.value.map((file) => file.uploadId).filter((id): id is string => !!id),
    });
    emit('success');
    handleClose();
  } catch (error: unknown) {
    if (isErrorResponse(error)) {
      errors.value = error.data.errors;
    }
  } finally {
    isLoading.value = false;
  }
};
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="t('payments.external.decline_title')"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div class="t:flex t:items-start t:gap-8">
          <UiIcon
            name="times"
            color="var(--text-negative)"
            :size="24"
          />
          <div
            class="decline-external-payment-popup__message"
            v-html="t('payments.external.decline_message', { affiliateEmail: props.affiliateEmail })"
          />
        </div>
        <UiTextarea
          v-model="form.comment"
          :label="t('payments.external.comment')"
          :placeholder="t('payments.external.comment_placeholder')"
          :error="errors.comment"
        />
        <UploadFile
          v-model="files"
          :title="t('payments.uploadFilesPopup.label')"
          accept=".pdf, .doc, .docx, .png, .jpg, .jpeg"
          is-dragger
          multiple
          @is-loading="isFilesLoading = $event"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          type="negative"
          size="big"
          icon="check"
          :disabled="isFilesLoading"
          @click="handleDecline"
        >
          {{ t('payments.external.decline') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.decline-external-payment-popup {
  &__message {
    color: var(--text-body);
    font-size: 14px;
    line-height: 24px;

    b {
      font-weight: 500;
    }
  }
}
</style>
